import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';

import { ROUTES } from '@/constants/routes';
import { Alert } from '@/design-system/components/Alert';
import { Loader } from '@/design-system/components/Loader';
import { ResetPasswordForm } from '@/features/authentication/ResetPasswordForm';
import { AuthenticationLayout } from '@/layouts/AuthenticationLayout';
import { authenticationService } from '@/services/authentication-service';
import type { ResetTokenState } from '@/services/reset-token';

const INVALID_COPY: Record<Exclude<ResetTokenState, 'valid'>, { title: string; body: string }> = {
  expired: {
    title: 'This reset link has expired',
    body: 'Password reset links are valid for 60 minutes. Request a new link to continue.',
  },
  used: {
    title: 'This reset link has already been used',
    body: 'Each reset link works once. Request a new link if you still need to change your password.',
  },
  superseded: {
    title: 'This reset link has been replaced',
    body: 'A newer reset link was requested for this account, which cancelled this one.',
  },
  unknown: {
    title: 'This reset link cannot be used',
    body: 'The link is malformed or no longer recognised. Request a new reset link.',
  },
};

function ResetPasswordPage() {
  const { token } = Route.useParams();
  const { data: state, isLoading } = useQuery({
    queryKey: ['reset-token', token],
    queryFn: () => authenticationService.inspectResetToken(token),
  });

  return (
    <AuthenticationLayout title="Reset Password" description="Choose a new administrator password.">
      {isLoading || state === undefined ? (
        <Loader className="py-8" />
      ) : state === 'valid' ? (
        <ResetPasswordForm token={token} />
      ) : (
        <div className="space-y-4">
          <Alert severity="error" title={INVALID_COPY[state].title}>
            {INVALID_COPY[state].body}
          </Alert>
          <Link
            to={ROUTES.forgotPassword}
            className="block text-center text-caption font-medium text-brand hover:underline"
          >
            Request a new reset link
          </Link>
          <Link
            to={ROUTES.signIn}
            className="block text-center text-caption font-medium text-text-secondary hover:underline"
          >
            Back to Sign In
          </Link>
        </div>
      )}
    </AuthenticationLayout>
  );
}

export const Route = createFileRoute('/admin/reset-password/$token')({
  component: ResetPasswordPage,
});
